import {Component, Input, OnInit, OnDestroy, WritableSignal, signal, inject} from '@angular/core';
import {TranslateModule} from "@ngx-translate/core";
import {SearchContainer} from "../../../core/definitions/search-container";
import {FilterGroupComponent} from "./filter-group/filter-group.component";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {SearchFacetService} from "../../search-facet.service";
import {
  CheckFilterGroup,
  FilterGroupHierarchyNode,
  HierarchicFilterGroup,
  RangeGroup
} from "../../../core/definitions/search-objects";
import {SearchFilterService} from "../../search-filter.service";
import {SearchExecutorService} from "../../search-executor.service";
import {MatButton} from "@angular/material/button";
import {FilterRangeComponent} from "./filter-range/filter-range.component";
import {StoredSearchServiceImplementation} from "../../stored-search.service";
import {FilterHierarchyComponent} from "./filter-hierarchy/filter-hierarchy.component";
import {CategoryMenuItem} from "../filter-menu-category-selector/filter-menu-category-selector.component";
import {NgClass} from "@angular/common";
import {HierarchicFilterGroupService} from "../../hierarchic-filter-group.service";
import {SearchViewFilterService} from "../../search-view-filter.service";
import { MatDialog } from '@angular/material/dialog';
import { ExtendedSearchDbDialogComponent } from '../../extended-search-query-builder/extended-search-db-dialog/extended-search-db-dialog.component';

@Component({
    selector: 'app-filter-menu-filter-list',
    imports: [
        TranslateModule,
        TranslateModule,
        FilterGroupComponent,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        MatButton,
        FilterRangeComponent,
        FilterHierarchyComponent,
        NgClass
    ],
    templateUrl: './filter-menu-filter-list.component.html',
    styleUrl: './filter-menu-filter-list.component.scss'
})
export class FilterMenuFilterListComponent implements OnInit, OnDestroy{
  // Injections
  private readonly hierarchicFilterGroupService: HierarchicFilterGroupService = inject(HierarchicFilterGroupService);
  private readonly modal: MatDialog = inject(MatDialog);
  private readonly searchExecutorService: SearchExecutorService = inject(SearchExecutorService);
  private readonly searchFacetService: SearchFacetService = inject(SearchFacetService);
  private readonly searchFilterService: SearchFilterService = inject(SearchFilterService);
  private readonly searchViewFilterService: SearchViewFilterService = inject(SearchViewFilterService);

  @Input() fsi: StoredSearchServiceImplementation;
  @Input() loading: boolean;
  @Input() searchContainer: SearchContainer;
  @Input() subCategories: WritableSignal<CategoryMenuItem[]>;

  dialogRef = null;

  startExpandedSubCategories: boolean = true;
  staticHierarchicFilterGroup:  HierarchicFilterGroup;

  searchFailed: WritableSignal<boolean> = signal(false);

  ngOnInit() {
    this.searchExecutorService.subscribeToSearchResult(this.searchContainer, this.initialize);
  }

  ngOnDestroy() {
    this.searchExecutorService.unSubscribeToSearchResult(this.searchContainer, this.initialize);
  }

  initialize = () => {
    this.staticHierarchicFilterGroup = this.searchViewFilterService.getHierarchicFilterGroupFromSearchView(
      this.searchContainer.currentPathView.search_view);
    if (this.dialogRef !== null) {
      this.dialogRef.close();
    }
    const hierarchicGroup = this.searchContainer.filtersFacets.hierarchicFilterGroup;
    // Rebuild the tree after a stored search restore: lastSelectedNode is applied before
    // search, but the panel may already be expanded so expandedChange never fires.
    if (hierarchicGroup?.lastSelectedNode && !hierarchicGroup.childrenArray?.length) {
      this.hierarchicFilterGroupService.setHierarchicFilterGroup(this.searchContainer);
    }
  }

  async clearSetFilters() {
    this.searchFilterService.resetFilter(this.searchContainer);
    if (this.searchContainer.filtersFacets?.hierarchicFilterGroup) {
      this.searchContainer.filtersFacets.hierarchicFilterGroup.lastSelectedNode = null;
      if (this.searchContainer.filtersFacets.hierarchicFilterGroup.childrenArray?.length > 0) {
        this.checkHierarchicFiltersChecked(this.searchContainer.filtersFacets.hierarchicFilterGroup.childrenArray, true);
      }
    }

    await this.searchExecutorService.runSearch(this.searchContainer);
  }

  getCount(filterGroup: CheckFilterGroup) {
    return this.searchFacetService.getTotalCount(filterGroup, this.searchContainer);
  }

  filterGroupToggled(filterGroup: CheckFilterGroup) {
    filterGroup.enabled = true //.hasOwnProperty('enabled') ? !filterGroup.enabled : true;
    filterGroup.active = filterGroup.hasOwnProperty('active') ? filterGroup.active : false;

    if (filterGroup.enabled && !filterGroup.active) {
      this.setSearchSettings().then();
    }
  }

  async hierarchicFilterGroupPreExpanded(expanded: boolean) {
    const hierarchicGroup = this.searchContainer.filtersFacets.hierarchicFilterGroup;
    if (expanded) {
      // Tree was just restored from an existing search result. Do not run another search.
      if (hierarchicGroup?.childrenArray?.length) {
        return;
      }
      if (this.searchContainer?.extendedSearchParams?.db_search) {
        this.dialogRef = this.modal.open(ExtendedSearchDbDialogComponent, {
          panelClass: 'extended-search__db-dialog',
          disableClose: true,
          data: {
            failed: this.searchFailed
          }
        });

        this.dialogRef.afterClosed().subscribe(() => {
        });
      }

      const lastSelectedNode = hierarchicGroup?.lastSelectedNode;
      this.searchContainer.filtersFacets.hierarchicFilterGroup = {
        ...this.staticHierarchicFilterGroup,
        lastSelectedNode
      };
      if (this.searchContainer.searchResult) {
        this.hierarchicFilterGroupService.setHierarchicFilterGroup(this.searchContainer);
        return;
      }
      this.setSearchSettings().then(() => {
        if (!this.searchContainer.filtersFacets.hierarchicFilterGroup?.childrenArray?.length ||
            this.staticHierarchicFilterGroup?.title !== this.searchContainer.filtersFacets.hierarchicFilterGroup?.title) {
          this.hierarchicFilterGroupService.setHierarchicFilterGroup(this.searchContainer);
        }
      });
    } else if (!expanded && hierarchicGroup?.childrenArray?.length) {
      this.searchContainer.filtersFacets.hierarchicFilterGroup = null;
    }
  }

  filterGroupPreExpanded(expanded: boolean, filterGroup: CheckFilterGroup) {
    if (expanded && filterGroup.enabled) {
      setTimeout(() => {
        this.setSearchSettings().then(() => {
        });
      }, 300);
    } else if (!expanded && filterGroup.enabled) {
      filterGroup.enabled = false;
    }
  }

  filterRangeGroupToggled(filterGroup: RangeGroup) {
    filterGroup.enabled = filterGroup.hasOwnProperty('enabled') ? !filterGroup.enabled : true;

    if (filterGroup.enabled) {
      this.searchExecutorService.runSearch(this.searchContainer).then();
    }
  }

  hasFiltersChecked(): boolean {
    if (this.searchContainer.filtersFacets?.filterGroups?.length > 0) {
      for (const filter of this.searchContainer.filtersFacets.filterGroups) {
        if (filter.checkFilters?.length > 0) {
          for (const f of filter.checkFilters) {
            if (f.checked) {
              return true;
            }
          }
        }
      }
    }

    if (this.searchContainer.filtersFacets?.facetRangeGroups?.length > 0) {
      for (const range of this.searchContainer.filtersFacets.facetRangeGroups) {
        if (range.facet_ranges?.length > 0) {
          for (const facet of range.facet_ranges) {
            if (facet.checked) {
              return true;
            }
          }
        }
      }
    }

    if (this.searchContainer.filtersFacets?.hierarchicFilterGroup?.childrenArray?.length > 0) {
      if (this.searchContainer.filtersFacets?.hierarchicFilterGroup?.facet?.selected) {
        return true;
      }

      return this.checkHierarchicFiltersChecked(this.searchContainer.filtersFacets?.hierarchicFilterGroup?.childrenArray);
    }

    return false;
  }

  private checkHierarchicFiltersChecked(filterGroup: FilterGroupHierarchyNode[], deselect: boolean = false): boolean {
    let isChecked = false;

    for (const filter of filterGroup) {
      if (filter.facet?.selected) {
        if (deselect) {
          filter.facet.selected = false;
          filter.open = false;
        }
        isChecked = true;
      }

      if(filter.childrenArray?.length) {
        isChecked = this.checkHierarchicFiltersChecked(filter.childrenArray);
      }
    }

    return isChecked;
  }


  private async setSearchSettings() {
    await this.searchExecutorService.runSearch(this.searchContainer);
    this.searchFilterService.checkMenusFromCheckedFilters(this.searchContainer);
    await this.searchFilterService.setCheckFilterGroups(this.searchContainer, false, true);
  }

  protected readonly Object = Object;
}
